import { Controller } from "@hotwired/stimulus"

// Swipe-to-delete estilo iOS para filas de notificación del account
// (doc: _docs/features/notifications/account_notifications.md).
// Pointer Events sobre la capa __cell: el gesto solo se captura cuando la
// intención es horizontal (touch-action: pan-y deja el scroll al browser).

const ACTION_WIDTH = 88
const OPEN_THRESHOLD = ACTION_WIDTH / 2
const FULL_SWIPE_RATIO = 0.55
const FLICK_VELOCITY = 0.6 // px/ms
const COLLAPSE_MS = 220

// A lo sumo una fila abierta a la vez, como en las listas nativas
let openRow = null

export default class extends Controller {
  static targets = ["cell"]
  static values = { url: String, readUrl: String }

  connect() {
    this.position = 0
    this.dragging = false
    this.suppressClick = false
    this.pointerId = null
    // El snapshot de Turbo se toma antes del disconnect: cerrar acá evita
    // cachear la fila con el swipe abierto
    this.beforeCache = () => this.close()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    if (openRow === this) openRow = null
  }

  pointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return
    this.pointerId = event.pointerId
    this.startX = event.clientX
    this.startY = event.clientY
    this.startPosition = this.position
    this.dragging = false
    this.decided = false
    this.suppressClick = false
    this.lastX = event.clientX
    this.lastTime = event.timeStamp
    this.velocity = 0
  }

  pointerMove(event) {
    if (this.pointerId !== event.pointerId) return
    const dx = event.clientX - this.startX
    const dy = event.clientY - this.startY

    if (!this.decided) {
      // Scroll vertical dominante: soltar el gesto y no volver a mirarlo
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
        this.pointerId = null
        return
      }
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        // A la derecha desde cerrada no hay gesto: la fila queda estática y
        // el edge-swipe nativo de iOS (volver atrás) sigue disponible
        if (dx > 0 && this.startPosition === 0) {
          this.pointerId = null
          return
        }
        this.decided = true
        this.dragging = true
        this.element.classList.add("is-dragging")
        try { this.cellTarget.setPointerCapture(event.pointerId) } catch { /* pointer ya liberado */ }
        if (openRow && openRow !== this) openRow.close()
      } else {
        return
      }
    }

    const dt = event.timeStamp - this.lastTime
    if (dt > 0) this.velocity = (event.clientX - this.lastX) / dt
    this.lastX = event.clientX
    this.lastTime = event.timeStamp

    // Nunca positivo: hacia la derecha solo se puede cerrar (de -88 a 0)
    const next = Math.min(this.startPosition + dx, 0)
    this.#translate(next)
  }

  pointerUp(event) {
    if (this.pointerId !== event.pointerId) return
    this.pointerId = null
    if (!this.dragging) return
    this.dragging = false
    this.element.classList.remove("is-dragging")
    this.suppressClick = true

    const width = this.element.offsetWidth
    const fullSwipe =
      this.position < -width * FULL_SWIPE_RATIO ||
      (this.velocity < -FLICK_VELOCITY && this.position < -ACTION_WIDTH)

    if (fullSwipe) {
      this.#delete()
    } else if (this.position < -OPEN_THRESHOLD || (this.velocity < -FLICK_VELOCITY && this.position < -12)) {
      this.open()
    } else {
      this.close()
    }
  }

  pointerCancel(event) {
    if (this.pointerId !== event.pointerId) return
    this.pointerId = null
    this.dragging = false
    this.element.classList.remove("is-dragging")
    this.position < -OPEN_THRESHOLD ? this.open() : this.close()
  }

  // El click llega después del pointerup del drag (o con otra fila abierta):
  // en ambos casos no debe navegar
  cellClick(event) {
    if (this.suppressClick || this.dragging) {
      event.preventDefault()
      event.stopPropagation()
      this.suppressClick = false
      return
    }
    if (openRow === this) {
      event.preventDefault()
      this.close()
      return
    }
    if (openRow) {
      event.preventDefault()
      openRow.close()
      return
    }
    this.#markRead()
  }

  // Tap normal en la fila: read implica seen; keepalive para que el POST
  // sobreviva al visit de Turbo cuando la fila navega a su URL
  #markRead() {
    if (!this.element.classList.contains("is-unread")) return
    if (!this.hasReadUrlValue || !this.readUrlValue) return

    this.element.classList.remove("is-unread")
    fetch(this.readUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
      keepalive: true
    }).catch((error) => console.error("No se pudo marcar como leída:", error))
  }

  cancelNativeDrag(event) {
    event.preventDefault()
  }

  // Botón "Eliminar" del swipe y ✕ de hover/teclado
  remove() {
    this.#delete()
  }

  open() {
    if (openRow && openRow !== this) openRow.close()
    openRow = this
    this.element.classList.add("is-open")
    this.#settle(-ACTION_WIDTH)
  }

  close() {
    if (openRow === this) openRow = null
    this.element.classList.remove("is-open")
    this.#settle(0)
  }

  #translate(x) {
    this.position = x
    this.cellTarget.style.transform = `translate3d(${x}px, 0, 0)`
  }

  #settle(x) {
    this.position = x
    this.cellTarget.style.transform = x === 0 ? "" : `translate3d(${x}px, 0, 0)`
  }

  async #delete() {
    if (openRow === this) openRow = null
    this.element.classList.add("is-deleting")
    this.#settle(-this.element.offsetWidth)

    let total = null
    try {
      const response = await fetch(this.urlValue, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })
      // 404 = ya borrada en otra pestaña/dispositivo: remover igual
      if (!response.ok && response.status !== 404) throw new Error(`HTTP ${response.status}`)
      if (response.ok) total = (await response.json()).total
    } catch (error) {
      console.error("No se pudo borrar la notificación:", error)
      this.element.classList.remove("is-deleting")
      this.close()
      return
    }

    // Colapso de altura antes de remover para que la lista se cierre suave
    this.element.style.height = `${this.element.offsetHeight}px`
    this.element.offsetHeight // reflow: fija la altura antes de transicionar
    this.element.classList.add("is-collapsing")
    this.element.style.height = "0px"

    setTimeout(() => {
      // El dispatch va antes del remove: el evento tiene que burbujear desde
      // una fila todavía en el DOM (lo escucha notification-list)
      this.dispatch("deleted", { detail: { total }, bubbles: true })
      this.element.remove()
    }, COLLAPSE_MS)
  }
}
